/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  makeInitialStackFooterOptions,
  makeInitialStackHeaderOptions,
  makeInitialStackSeriesOptions,
} from './options/stack-options';
import { createContext } from '../react-context';
import { createSlots } from '../react-slots';
import { makeInitialOverlayOptions } from './options/overlay-options';
import { selectSettings } from '@innovyze/stylovyze';
import { useControlledRender, useStableRef } from '../lib-utils';
import { useSync, SyncModule } from './sync-module';
import * as React from 'react';
import Highcharts from 'highcharts';
import Highstock from 'highcharts/highstock';
import styled from 'styled-components';

import type { EventHandlers, StackableChartState } from './chart-types';
import { useGlobalization } from '../../../i18n';

SyncModule(Highcharts);
SyncModule(Highstock);

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Create Stackable Chart
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const createStackableChart = <
  RootContext extends Record<string, unknown> = Record<string, unknown>,
>(
  chartComponentName: string
) => {
  type InstanceId =
    | 'overlay'
    | 'stacked-header'
    | 'stacked-footer'
    | `stacked-series-${string}`;

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Slots
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const [_Slots, Slot, _useSlots] = createSlots(chartComponentName, [
    'series',
    'axes',
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Chart Root Context
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const [_RootInternalsProvider, _useRootInternals] = createContext<{
    containerElementRef: React.RefObject<HTMLDivElement>;
    instanceMapRef: React.MutableRefObject<Map<InstanceId, Highcharts.Chart>>;
  }>(chartComponentName);

  const [_RootContextProvider, useRootContext] =
    createContext<RootContext>(chartComponentName);

  const [_StackableContextProvider, useStackableContext] = createContext<{
    stackedRef: React.MutableRefObject<boolean | undefined>;
    internalStateRef: React.MutableRefObject<StackableChartState>;
  }>(chartComponentName);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Chart Instance Context
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const [_InstanceInternalsProvider, _useInstanceInternals] = createContext<{
    instanceRef: React.MutableRefObject<Highcharts.Chart | undefined>;
  }>(chartComponentName);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Chart Root Component
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const Root = (
    props: RootContext & { children: React.ReactNode }
  ): React.ReactElement => {
    const { children, ...contextProps } = props;
    const containerElementRef = React.useRef<HTMLDivElement>(null);
    const instanceMapRef = React.useRef<Map<InstanceId, Highcharts.Chart>>(
      new Map()
    );

    React.useLayoutEffect(() => {
      const mouseLeaveEventHandler = () => {
        instanceMapRef.current.forEach((instance) => {
          if (instance) {
            if (instance?.options && instance?.pointer) {
              instance.series.forEach(function (series) {
                instance?.tooltip?.hide?.();
                instance?.xAxis[0]?.hideCrosshair?.();
                series.setState('normal');
                series?.points?.forEach((point) => {
                  point.setState?.('normal');
                });
              });
            }
          }
        });
      };

      containerElementRef.current?.addEventListener(
        'mouseleave',
        mouseLeaveEventHandler
      );

      return () => {
        containerElementRef.current?.removeEventListener(
          'mouseleave',
          mouseLeaveEventHandler
        );
      };
    }, [containerElementRef, instanceMapRef]);

    React.useLayoutEffect(() => {
      if (containerElementRef.current) {
        const resizeObserver = new ResizeObserver(
          (entries: ResizeObserverEntry[]) => {
            window.requestAnimationFrame((): void | undefined => {
              if (!Array.isArray(entries) || !entries.length) {
                return;
              }
              window.dispatchEvent(new Event('resize'));
            });
          }
        );

        resizeObserver.observe(containerElementRef.current);

        return () => {
          resizeObserver.disconnect();
        };
      }
    }, [containerElementRef]);

    return (
      <_RootInternalsProvider
        containerElementRef={containerElementRef}
        instanceMapRef={instanceMapRef}>
        <_RootContextProvider {...(contextProps as unknown as RootContext)}>
          <_Layout.Root ref={containerElementRef}>{children}</_Layout.Root>
        </_RootContextProvider>
      </_RootInternalsProvider>
    );
  };

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Chart Instance Component
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const _Instance = React.forwardRef<
    { chart: Highcharts.Chart | undefined },
    {
      children: React.ReactNode;
      id: InstanceId;
      initialOptions: Highcharts.Options;
      chartConstructor: typeof Highcharts.chart;
    }
  >((props, ref) => {
    const root = _useRootInternals(chartComponentName);
    const chartConstructorRef = React.useRef(props.chartConstructor);
    const containerElementRef = React.useRef<HTMLDivElement>(null);
    const initialOptionsRef = React.useRef(props.initialOptions);
    const instanceRef = React.useRef<Highcharts.Chart>();
    const { companySettings } = selectSettings();
    const { t } = useGlobalization();

    /**
     * This MUST be a layout effect as we need the instance to be ready BEFORE
     * any other effect runs that may potentially run an instance update.
     **/
    React.useLayoutEffect(() => {
      if (containerElementRef.current) {
        instanceRef.current = chartConstructorRef.current(
          containerElementRef.current,
          initialOptionsRef.current
        );

        root.instanceMapRef.current.set(props.id, instanceRef.current);
      }
    }, [
      props.id,
      root.instanceMapRef,
      instanceRef,
      containerElementRef,
      initialOptionsRef,
    ]);

    React.useLayoutEffect(() => {
      instanceRef.current?.update({
        time: { timezone: companySettings.timeZoneIANA },
        lang: {
          weekdays: [
            t('Sunday'),
            t('Monday'),
            t('Tuesday'),
            t('Wednesday'),
            t('Thursday'),
            t('Friday'),
            t('Saturday'),
          ],
          months: [
            t('January'),
            t('February'),
            t('March'),
            t('April'),
            t('May'),
            t('June'),
            t('July'),
            t('August'),
            t('September'),
            t('October'),
            t('November'),
            t('December'),
          ],
          shortMonths: [
            t('Jan'),
            t('Feb'),
            t('Mar'),
            t('Apr'),
            t('May'),
            t('Jun'),
            t('Jul'),
            t('Aug'),
            t('Sep'),
            t('Oct'),
            t('Nov'),
            t('Dec'),
          ],
          loading: t('Loading....'),
          resetZoom: t('Reset zoom'),
          rangeSelectorZoom: t('Zoom'),
        },
      });
    }, [companySettings.timeZoneIANA]);

    /**
     * Contrary to the creation operation, destroying the chart MUST be done
     * as the last step. Otherwise, if a hook tries to perform an action on
     * a chart that is destroyed, Highcharts will error out.
     *
     * This happens when we have to destroy series when switching from stack
     * to overlay.
     **/
    React.useEffect(() => {
      return () => {
        instanceRef.current?.destroy();
        instanceRef.current = undefined;

        root.instanceMapRef.current.delete(props.id);
      };
    }, [props.id, root.instanceMapRef, instanceRef]);

    React.useImperativeHandle(
      ref,
      () => ({
        get chart() {
          return instanceRef.current;
        },
      }),
      []
    );

    return (
      <_InstanceInternalsProvider instanceRef={instanceRef}>
        <_Layout.Instance ref={containerElementRef} />
        {props.children}
      </_InstanceInternalsProvider>
    );
  });

  _Instance.displayName = '_Instance';

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Stacked Chart
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const Stackable = React.forwardRef<
    { chart: Highcharts.Chart | undefined },
    EventHandlers & {
      children: React.ReactNode;
      chartConstructor: typeof Highcharts.chart;
      stacked?: boolean;
      initialOptions?: Highcharts.Options;
    }
  >((props, ref): React.ReactElement => {
    const internalStateRef = React.useRef<StackableChartState>({
      xAxis: { min: undefined, max: undefined },
      yAxis: { min: undefined, max: undefined },
      hiddenSeries: [],
    });

    const stackedRef = useStableRef(props.stacked);
    const eventHandlersRef = useStableRef<EventHandlers>({
      onXAxisExtremesChange: (min, max) => {
        internalStateRef.current.xAxis.min = min;
        internalStateRef.current.xAxis.max = max;
        props.onXAxisExtremesChange?.(min, max);
      },
      onYAxisExtremesChange: (min, max) => {
        internalStateRef.current.yAxis.min = min;
        internalStateRef.current.yAxis.max = max;
        props.onYAxisExtremesChange?.(min, max);
      },
      onSeriesVisibilityChange: (index, type) => {
        if (type === 'show') {
          internalStateRef.current.hiddenSeries =
            internalStateRef.current.hiddenSeries.filter(
              (seriesIndex) => seriesIndex !== index
            );
        } else {
          internalStateRef.current.hiddenSeries.push(index);
        }
        props.onSeriesVisibilityChange?.(index, type);
      },
    });

    return (
      <_StackableContextProvider
        stackedRef={stackedRef}
        internalStateRef={internalStateRef}>
        {props.stacked ? (
          <_Slots _children={props.children}>
            <_Stacked
              ref={ref}
              chartConstructor={props.chartConstructor}
              eventHandlersRef={eventHandlersRef}
              initialOptions={props.initialOptions}
            />
          </_Slots>
        ) : (
          <_Overlay
            ref={ref}
            chartConstructor={props.chartConstructor}
            eventHandlersRef={eventHandlersRef}
            initialOptions={props.initialOptions}>
            {props.children}
          </_Overlay>
        )}
      </_StackableContextProvider>
    );
  });

  Stackable.displayName = 'Stackable';

  const _Overlay = React.forwardRef<
    { chart: Highcharts.Chart | undefined },
    {
      children: React.ReactNode;
      chartConstructor: typeof Highcharts.chart;
      eventHandlersRef: React.MutableRefObject<EventHandlers>;
      initialOptions?: Highcharts.Options;
    }
  >((props, ref): React.ReactElement => {
    return (
      <_Instance
        id="overlay"
        ref={ref}
        initialOptions={makeInitialOverlayOptions(
          props.eventHandlersRef,
          props.initialOptions
        )}
        chartConstructor={props.chartConstructor}>
        {props.children}
      </_Instance>
    );
  });

  _Overlay.displayName = '_Overlay';

  const _Stacked = React.forwardRef<
    { chart: Highcharts.Chart | undefined },
    {
      chartConstructor: typeof Highcharts.chart;
      eventHandlersRef: React.MutableRefObject<EventHandlers>;
      initialOptions?: Highcharts.Options;
    }
  >((props, ref): React.ReactElement => {
    const stackableContext = useStackableContext(chartComponentName);
    const root = _useRootInternals(chartComponentName);
    const slots = _useSlots(chartComponentName).getSlotsMap();
    const seriesChildrenArray: React.ReactElement[] = [];
    const childrenArray: React.ReactElement[] = [];

    slots.forEach((children, key) => {
      const _children = React.Children.toArray(children);
      const targetArray =
        key === 'series' ? seriesChildrenArray : childrenArray;

      _children.forEach((child) => {
        if (React.isValidElement(child)) {
          targetArray.push(child);
        }
      });
    });

    const { onExtremesSync } = useSync(
      root.containerElementRef,
      root.instanceMapRef
    );

    const onExtremesSyncRef = useStableRef(onExtremesSync);

    const [_, render] = useControlledRender();
    const onVisibilityChangeRef = useStableRef<
      EventHandlers['onSeriesVisibilityChange']
    >(() => {
      render();
    });

    React.useLayoutEffect(() => {
      window.dispatchEvent(new Event('resize'));
    });

    return (
      <_Layout.Stacked.Root>
        <_Layout.Stacked.Header>
          <_Instance
            id="stacked-header"
            initialOptions={makeInitialStackHeaderOptions(
              onExtremesSyncRef,
              props.initialOptions
            )}
            chartConstructor={props.chartConstructor}>
            {childrenArray.map((child, index) => {
              return React.cloneElement(child, {
                ...child.props,
                key: index,
              });
            })}
            {seriesChildrenArray.map((child, index) => {
              return React.cloneElement(child, {
                ...child.props,
                key: index,
              });
            })}
          </_Instance>
        </_Layout.Stacked.Header>
        <_Layout.Stacked.ScrollArea>
          {seriesChildrenArray.map((child, index) => {
            const hiddenByControlledProp = child.props.hidden;
            const hiddenByInternalState =
              stackableContext.internalStateRef.current.hiddenSeries.includes(
                index
              );

            if (hiddenByControlledProp || hiddenByInternalState) {
              return null;
            }

            return (
              <_Layout.Stacked.Series
                key={`stacked-series-${child.props.id || index}`}>
                <_Instance
                  id={`stacked-series-${child.props.id || index}`}
                  initialOptions={makeInitialStackSeriesOptions(
                    onExtremesSyncRef,
                    props.initialOptions
                  )}
                  chartConstructor={props.chartConstructor}>
                  {childrenArray.map((child, index) => {
                    return React.cloneElement(child, {
                      ...child.props,
                      key: index,
                    });
                  })}
                  {React.cloneElement(child)}
                </_Instance>
              </_Layout.Stacked.Series>
            );
          })}
        </_Layout.Stacked.ScrollArea>
        <_Layout.Stacked.Footer>
          <_Instance
            id="stacked-footer"
            ref={ref}
            initialOptions={makeInitialStackFooterOptions(
              props.eventHandlersRef,
              onVisibilityChangeRef,
              onExtremesSyncRef,
              props.initialOptions
            )}
            chartConstructor={props.chartConstructor}>
            {childrenArray.map((child, index) => {
              return React.cloneElement(child, {
                ...child.props,
                key: index,
              });
            })}
            {seriesChildrenArray.map((child, index) => {
              return React.cloneElement(child, {
                ...child.props,
                key: index,
              });
            })}
          </_Instance>
        </_Layout.Stacked.Footer>
      </_Layout.Stacked.Root>
    );
  });

  _Stacked.displayName = '_Stacked';

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Instance Hooks
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const useInstance = (consumerName: string) => {
    const rootInternals = _useRootInternals(consumerName);
    const instanceInternals = _useInstanceInternals(consumerName);

    const where = React.useCallback(
      (instanceIds?: RegExp | (InstanceId | RegExp)[]) => {
        if (instanceIds === undefined) {
          return [...rootInternals.instanceMapRef.current.values()];
        }

        const _instanceIds = Array.isArray(instanceIds)
          ? instanceIds
          : [instanceIds];

        const matchingInstances: Highcharts.Chart[] = [];
        const instanceKeys = Object.keys(rootInternals.instanceMapRef.current);

        for (const instanceId of _instanceIds) {
          if (typeof instanceId === 'string') {
            if (rootInternals.instanceMapRef.current.has(instanceId)) {
              matchingInstances.push(
                rootInternals.instanceMapRef.current.get(instanceId)!
              );
            }
          } else {
            const matchingKeys = instanceKeys.filter((key) =>
              instanceId.test(key)
            );
            for (const matchingKey of matchingKeys) {
              matchingInstances.push(
                rootInternals.instanceMapRef.current.get(
                  matchingKey as InstanceId
                )!
              );
            }
          }
        }

        return matchingInstances;
      },
      []
    );

    const get = React.useCallback(() => {
      return instanceInternals.instanceRef.current;
    }, []);

    return {
      get,
      where,
    };
  };

  return {
    Root,
    Slot,
    Stackable,
    useInstance,
    useRootContext,
    useStackableContext,
    _useRootInternals,
  } as const;
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Layout Components
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const _Layout = {
  Root: styled.div`
    align-items: stretch;
    background-color: #fff;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    overflow: hidden;
    padding: 15px;
    position: relative;
    width: 100%;

    & * {
      box-sizing: border-box;
    }
  `,
  Instance: styled.div`
    height: 100%;
    width: 100%;
  `,
  Stacked: {
    Root: styled.div`
      align-items: center;
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: space-between;
      width: 100%;
    `,
    Header: styled.div<{ withTitle?: boolean }>`
      height: ${(props) => (props.withTitle ? '80px' : '40px')};
      width: 100%;
    `,
    Footer: styled.div`
      height: 120px;
      width: 100%;
    `,
    Series: styled.div`
      flex: 1 1 0;
      min-height: 160px;
    `,
    ScrollArea: styled.div`
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      flex: 1 1 0;
      overflow: auto;
      width: 100%;
    `,
  },
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Module Exports
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export { createStackableChart };
